/**
 * Ground Truth API Client Service
 * Handles all API calls related to ground truth management
 */

import {
  IMasterGroundTruth,
  GroundTruthListResponse,
  GroundTruthFilters,
  GroundTruthUpdatePayload,
  GroundTruthCreatePayload,
  GroundTruthVersionHistoryResponse,
  GroundTruthComparisonResponse,
  GroundTruthStatistics
} from '../types/groundTruth';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Helper function to handle API responses
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get authentication headers
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  const apiKey = import.meta.env.VITE_API_KEY || '';

  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
    'x-api-key': apiKey
  };
}

/**
 * Ground Truth API Service
 */
export const GroundTruthService = {
  /**
   * Get paginated list of ground truth records
   */
  async getList(filters: GroundTruthFilters = {}): Promise<GroundTruthListResponse> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.document_type) params.append('document_type', filters.document_type);
    if (filters.validation_status) params.append('validation_status', filters.validation_status);
    if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.source) params.append('source', filters.source);

    const response = await fetch(
      `${API_BASE}/ground-truth?${params.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<GroundTruthListResponse>(response);
  },

  /**
   * Get single ground truth record by ID
   */
  async getById(id: string): Promise<IMasterGroundTruth> {
    const response = await fetch(
      `${API_BASE}/ground-truth/${id}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<IMasterGroundTruth>(response);
  },

  /**
   * Get ground truth record by document ID
   */
  async getByDocumentId(documentId: string): Promise<IMasterGroundTruth> {
    const response = await fetch(
      `${API_BASE}/ground-truth/document/${encodeURIComponent(documentId)}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<IMasterGroundTruth>(response);
  },

  /**
   * Create new ground truth record
   */
  async create(data: GroundTruthCreatePayload): Promise<IMasterGroundTruth> {
    const response = await fetch(
      `${API_BASE}/ground-truth`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );

    return handleResponse<IMasterGroundTruth>(response);
  },

  /**
   * Update ground truth record
   */
  async update(id: string, data: GroundTruthUpdatePayload): Promise<IMasterGroundTruth> {
    const response = await fetch(
      `${API_BASE}/ground-truth/${id}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );

    return handleResponse<IMasterGroundTruth>(response);
  },

  /**
   * Delete ground truth record
   */
  async delete(id: string): Promise<{ success: boolean }> {
    const response = await fetch(
      `${API_BASE}/ground-truth/${id}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<{ success: boolean }>(response);
  },

  /**
   * Get version history for a ground truth record
   */
  async getVersionHistory(
    id: string,
    limit = 20,
    offset = 0
  ): Promise<GroundTruthVersionHistoryResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });

    const response = await fetch(
      `${API_BASE}/ground-truth/${id}/versions?${params.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<GroundTruthVersionHistoryResponse>(response);
  },

  /**
   * Compare two versions of a ground truth record
   */
  async compareVersions(
    id: string,
    versionA: number,
    versionB: number
  ): Promise<GroundTruthComparisonResponse> {
    const response = await fetch(
      `${API_BASE}/ground-truth/${id}/compare?versionA=${versionA}&versionB=${versionB}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<GroundTruthComparisonResponse>(response);
  },

  /**
   * Get ground truth statistics
   */
  async getStatistics(): Promise<GroundTruthStatistics> {
    const response = await fetch(
      `${API_BASE}/ground-truth/statistics`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    return handleResponse<GroundTruthStatistics>(response);
  },

  /**
   * Batch validate ground truth records
   */
  async batchValidate(documentIds: string[]): Promise<{
    found: string[];
    notFound: string[];
  }> {
    const response = await fetch(
      `${API_BASE}/ground-truth/batch-validate`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ documentIds })
      }
    );

    return handleResponse<{ found: string[]; notFound: string[] }>(response);
  },

  /**
   * Get unique source names for filter dropdown
   */
  async getUniqueSources(): Promise<string[]> {
    const response = await fetch(
      `${API_BASE}/ground-truth/sources`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    const data = await handleResponse<{ sources: string[] }>(response);
    return data.sources;
  },

  /**
   * Export ground truth data (CSV/JSON)
   */
  async export(format: 'csv' | 'json' = 'json', filters: GroundTruthFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.document_type) params.append('document_type', filters.document_type);
    if (filters.validation_status) params.append('validation_status', filters.validation_status);
    if (filters.source) params.append('source', filters.source);
    if (filters.source_name) params.append('source_name', filters.source_name);

    const response = await fetch(
      `${API_BASE}/ground-truth/export/${format}?${params.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.blob();
  }
};

/**
 * Helper function to handle optimistic locking conflicts
 */
export async function updateWithRetry(
  id: string,
  updateFn: (current: IMasterGroundTruth) => GroundTruthUpdatePayload,
  maxRetries = 3
): Promise<IMasterGroundTruth> {
  let attempts = 0;

  while (attempts < maxRetries) {
    try {
      // Get current version
      const current = await GroundTruthService.getById(id);

      // Apply update function to get new data
      const updateData = updateFn(current);

      // Attempt update
      return await GroundTruthService.update(id, updateData);
    } catch (error: any) {
      attempts++;

      // If not a conflict error or max retries reached, throw
      if (!error.message?.includes('Concurrent update') || attempts >= maxRetries) {
        throw error;
      }

      // Wait briefly before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempts)));
    }
  }

  throw new Error('Max retries exceeded for optimistic locking');
}

/**
 * Helper function to download export file
 */
export function downloadExport(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default GroundTruthService;